import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import classNames from 'classnames';
import merge from 'lodash/merge';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Constants
import { IntegrationsList } from 'Shared/constants/IntegrationsList.constants';
import PageUrlsConstants from 'Shared/constants/PageUrls.constants';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useApp } from 'Shared/utils/hooks/use-app';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Types
import { Integration, IntegrationType } from 'Shared/types/DataSource.type';
import { FilterType } from './IntegrationsList';

// Actions
import { disconnectDataSource } from 'Shared/actions/data_sources';
import { disconnectNotification, disconnectOrganizationNotificationDestination } from 'Shared/actions/notifications';

// Components
import { WpButton } from 'Wpds/components/button';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import WpBadge from 'Wpds/components/badge/WpBadge';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import { WpButtonDropdown } from 'Wpds/components/button';

// Styles
import './IntegrationsListItem.scss';

// Tracking
import { trackClickedConnectApp, trackRequestedIntegrationAccess } from 'Shared/tracking/tracking';
import { useOrganizationSubscription } from 'Shared/utils/hooks/use-organization-subscription';

type Props = {
  integration: Integration;
  classnames?: string;
  redirectUrl?: string;
  filters?: FilterType;
};

const IntegrationsListItem = ({ integration, classnames, filters, redirectUrl }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isOrgAdmin } = useCurrentUser();
  const { isFreePlan } = useOrganizationSubscription();

  const { intercomContactSalesEventName } = useApp();
  const [isConnectModalOpen, toggleIsConnectModalOpen] = useToggle(false);
  const [wasClicked, toggleWasClicked] = useToggle(false);
  const toRender = merge(integration, IntegrationsList[integration.slug]);
  const hasBadge = toRender.badge;
  const isConnected = toRender.connected;
  const hasLearnMoreLink = toRender.learnMoreLink;

  const {
    [toRender.flag]: flag,
    [toRender.orgConnectFlag]: allowOrgConnectFlag,
    [toRender.mustRequestAccessFlag]: mustRequestAccessFlag,
    featureFlagAiToolkit: showAiToolkit,
  } = useFlags();

  const agendaAiDatasourceConnected = useSelector(
    state =>
      toRender.agendaAiDatasourceSlug &&
      state.data_sources.find(
        data_source => data_source.slug === toRender.agendaAiDatasourceSlug && data_source.connected
      )
  );
  const { featureFlagAgendaAi } = useFlags();

  const showAiToolkitBadge = toRender.aiToolkit;
  const showPremiumBadge = toRender.premium;
  const ConnectModal = toRender.connectModal;
  const description =
    featureFlagAgendaAi && toRender.agendaAiDescription ? toRender.agendaAiDescription : toRender.description;

  const handleConnectClick = () => {
    trackClickedConnectApp(toRender.slug);

    if (ConnectModal) {
      toggleIsConnectModalOpen();
      return;
    }

    if (toRender.open_new_tab && toRender.path) {
      const element = document.createElement('a');
      element.href = toRender.path;
      element.target = '_blank';
      element.rel = 'noopener noreferrer';
      document.body.appendChild(element);
      element.click();
      element.remove();
      return;
    }

    if (toRender.path) {
      toggleWasClicked();
      // TODO: (josh) : in the future we should have a safeway to append
      // these URLs incase some query params already exist on the path
      const path: string = toRender.isCalendar
        ? `${toRender.path}&redirect_url=/user/integrations?set_recurring_meetings=true`
        : redirectUrl
        ? toRender.path.includes('?')
          ? `${toRender.path}&redirect_url=${redirectUrl}`
          : `${toRender.path}?redirect_url=${redirectUrl}`
        : toRender.path;

      window.location.href = path;
    }
  };

  const handleAgendAiConnectClick = () => {
    if (toRender.path) {
      toggleWasClicked();
      trackClickedConnectApp(toRender.agendaAiDatasourceSlug);
      const path = redirectUrl
        ? `${toRender.path}&agenda_ai=true&redirect_url=${redirectUrl}`
        : `${toRender.path}&agenda_ai=true`;
      window.location.href = path;
    }
  };

  const handleOrgConnectClick = () => {
    if (toRender.path) {
      toggleWasClicked();
      // TODO: (josh) : in the future we should have a safeway to append
      // these URLs incase some query params already exist on the path
      window.location.href = `${toRender.path}&create_organization_notification_destination=true`;
    }
  };

  const handleRequestAccessClick = () => {
    trackRequestedIntegrationAccess(toRender.slug);
  };

  const handleDisconnectClick = () => {
    if (toRender.handleDisconnect) {
      toRender.handleDisconnect();
    } else if (toRender.type === IntegrationType.data_source) {
      dispatch(disconnectDataSource(toRender.id, toRender.name));
    } else if (toRender.type === IntegrationType.notification_destination) {
      dispatch(disconnectNotification(toRender.id));
    }
  };

  const handleOrgDisconnectClick = () => {
    if (toRender.handleOrgDisconnect) {
      toRender.handleOrgDisconnect();
    } else if (toRender.type === IntegrationType.data_source) {
      // TODO: not currently supported
    } else if (toRender.type === IntegrationType.notification_destination) {
      dispatch(disconnectOrganizationNotificationDestination(toRender.id));
    }
  };

  const getCategories = () => {
    if (!toRender.categories) return null;
    return toRender.categories.join(', ');
  };

  const baseClassName = 'IntegrationsListItem';
  const classes = classNames(baseClassName, classnames);

  const getButtonHeader = () => {
    const handleUpgradeClick = () => {
      history.push(PageUrlsConstants.PLANS_PAGE);
    };

    if (isFreePlan && showPremiumBadge) {
      return (
        <WpButton variant="secondary" size="sm" onClick={handleUpgradeClick}>
          <WpIcon name="crown" />
          Upgrade
        </WpButton>
      );
    }

    if (toRender.organization_notification_destination_connected && toRender.allowOrgConnect) {
      return (
        <div className={`${baseClassName}__header__connected-wrapper`}>
          <div className={`${baseClassName}__header__connected-wrapper__label`}>
            <WpIcon name="check" /> Org Connected
          </div>
          {isOrgAdmin && (
            <WpDropdown placement="bottom-end">
              <WpDropdownToggle>
                <WpButton color="gray" variant="secondary" size="sm">
                  <WpIcon name="ellipsis-h-alt" />
                </WpButton>
              </WpDropdownToggle>
              <WpDropdownMenu>
                <WpDropdownMenuItem color="danger" onClick={handleOrgDisconnectClick}>
                  Disconnect Organization
                </WpDropdownMenuItem>
              </WpDropdownMenu>
            </WpDropdown>
          )}
        </div>
      );
    }

    if (toRender.agendaAiDatasourceSlug && featureFlagAgendaAi) {
      if (!isConnected) {
        return (
          <WpButton color="gray" variant="secondary" onClick={handleAgendAiConnectClick} loading={wasClicked} size="sm">
            Connect
          </WpButton>
        );
      }
      if (isConnected && !agendaAiDatasourceConnected) {
        return (
          <div className={`${baseClassName}__header__connected-wrapper`}>
            <div className={`${baseClassName}__header__connected-wrapper__label`}>
              <WpIcon name="check" /> Notifications Connected
              <span className={`${baseClassName}__header__connected-wrapper__label__connect-button`}>
                <WpButton
                  color="gray"
                  variant="secondary"
                  onClick={handleAgendAiConnectClick}
                  loading={wasClicked}
                  size="xs"
                >
                  Connect AgendaAI
                </WpButton>
              </span>
            </div>
            <WpDropdown placement="bottom-end">
              <WpDropdownToggle>
                <WpButton color="gray" variant="secondary" size="sm">
                  <WpIcon name="ellipsis-h-alt" />
                </WpButton>
              </WpDropdownToggle>
              <WpDropdownMenu>
                <WpDropdownMenuItem color="danger" onClick={handleDisconnectClick}>
                  Disconnect
                </WpDropdownMenuItem>
              </WpDropdownMenu>
            </WpDropdown>
          </div>
        );
      }
    }

    if (isConnected) {
      return (
        <div className={`${baseClassName}__header__connected-wrapper`}>
          <div className={`${baseClassName}__header__connected-wrapper__label`}>
            <WpIcon name="check" /> Connected
          </div>
          <WpDropdown placement="bottom-end">
            <WpDropdownToggle>
              <WpButton color="gray" variant="secondary" size="sm">
                <WpIcon name="ellipsis-h-alt" />
              </WpButton>
            </WpDropdownToggle>
            <WpDropdownMenu>
              <WpDropdownMenuItem color="danger" onClick={handleDisconnectClick}>
                Disconnect
              </WpDropdownMenuItem>
            </WpDropdownMenu>
          </WpDropdown>
        </div>
      );
    }

    if (toRender.allowOrgConnect && allowOrgConnectFlag && isOrgAdmin) {
      return (
        <WpButtonDropdown
          color="gray"
          variant="secondary"
          onClick={handleConnectClick}
          size="sm"
          loading={wasClicked}
          addOnDropdownMenu={
            <WpDropdownMenu>
              <WpDropdownMenuItem onClick={handleOrgConnectClick}>Connect for Organization</WpDropdownMenuItem>
            </WpDropdownMenu>
          }
        >
          Connect
        </WpButtonDropdown>
      );
    }

    if (mustRequestAccessFlag) {
      return (
        <WpButton
          color="gray"
          variant="secondary"
          dataAttributes={{ 'data-intercom': intercomContactSalesEventName }}
          onClick={handleRequestAccessClick}
          size="sm"
        >
          Request Access
        </WpButton>
      );
    }

    return (
      <WpButton color="gray" variant="secondary" onClick={handleConnectClick} loading={wasClicked} size="sm">
        Connect
      </WpButton>
    );
  };

  if (toRender.flag && !flag) return null;
  if (filters?.surfaceAI && !toRender.surfaceAI) return null;
  if (filters?.showOnly && !filters.showOnly.includes(toRender.slug)) return null;

  return (
    <>
      <WpContainer className={classes}>
        <WpContainerBody className={`${baseClassName}__body`}>
          <div className={`${baseClassName}__header`}>
            <img src={integration.logo} className={`${baseClassName}__header__logo`} />
            {getButtonHeader()}
          </div>
          <div className={`${baseClassName}__name-wrapper`}>
            <h5 className={`${baseClassName}__name-wrapper__name`}>{toRender.name}</h5>
            {(hasBadge || showPremiumBadge || showAiToolkitBadge) && (
              <div className={`${baseClassName}__name-wrapper__badges`}>
                {showPremiumBadge && (
                  <span className={`${baseClassName}__name-wrapper__badges__badge`}>
                    <WpBadge color="blue" size="sm" variant="bold">
                      Premium
                    </WpBadge>
                  </span>
                )}
                {showAiToolkitBadge && showAiToolkit && (
                  <span className={`${baseClassName}__name-wrapper__badges__badge`}>
                    <WpBadge color="purple" size="sm" variant="bold">
                      AI Toolkit
                    </WpBadge>
                  </span>
                )}
                {hasBadge && (
                  <span className={`${baseClassName}__name-wrapper__badges__badge`}>
                    <WpBadge color="purple" size="sm" variant="bold">
                      {toRender.badge}
                    </WpBadge>
                  </span>
                )}
              </div>
            )}
          </div>
          <div className={`${baseClassName}__description`}>
            {description}
            {hasLearnMoreLink && (
              <div className={`${baseClassName}__description__learn-more-link`}>
                <a target="_blank" rel="noopener noreferrer" href={toRender.learnMoreLink}>
                  Learn more
                </a>
              </div>
            )}
          </div>
          <p className={`${baseClassName}__categories`}>{getCategories()}</p>
        </WpContainerBody>
      </WpContainer>
      {ConnectModal && <ConnectModal isOpen={isConnectModalOpen} toggleIsOpen={toggleIsConnectModalOpen} />}
    </>
  );
};

export default IntegrationsListItem;
